import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import scss from './StackLineMemoryInfo.scss';
import IconMemory from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_stack_memory_icon.svg';
import { Button, Col, Input, message, Modal, Row, Tooltip } from 'antd';
import StackAi from 'svg/v2/newcs_aiassistant_aianalysis.svg';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';
import { sleep } from 'utils/sleep';
import { connect } from 'react-redux';
import { isNotNullish, isNullish } from 'utils/nullish';
import { RegisterInfoUtil } from 'components/exception/issue/RegisterInfo/RegisterInfo';
import useZuStore from 'store/zustand/zustand-store';
import { isAndroid } from 'utils/platform';

const StackLineMemoryInfo = ({
  reduxState,
  appId,
  crashId,
  lineObj,
  lineNumber,
  rva,
  pid,
  detailStackMemoryInfo,
  isMinidump,
}) => {
  const aiDrawerSlice = useZuStore().aiDrawer;

  const isSuper = !!reduxState.user.get('current').get('isSuper');

  const currentIssue = useMemo(() => {
    return reduxState.issue.get('current')?.toJS();
  }, [reduxState.issue]);

  const {
    minidumpRegisterInfoParseResult,
  } = currentIssue || {};

  const currentLineRegisterParseResult = (minidumpRegisterInfoParseResult || []).find((x, i) => {
    return lineNumber === i + 1;
  });
  const hasCurrentLineRegisterParseResult = isNotNullish(currentLineRegisterParseResult);
  const registerKvPairs = currentLineRegisterParseResult?.registerKvPairs || [];

  const {
    lineStack,
    stackMemoryInfo,
    asmCodeInfo,
    stackVariable: stackVariableJsonStr,
    originAddress, // 来自移动端非minidump的memoryData
    originMemoryData, // 来自移动端非minidump的memoryData
    contextValidity, //过滤展示有效的寄存器信息
  } = detailStackMemoryInfo || {};

  // 自动生成寄存器名称到索引的映射
  const generateRegisterIndexMap = () => {
      const map = {};

      for (let i = 0; i < 29; i++) {
          map[`x${i}`] = i;
      }

      map["fp"] = 29;
      map["lr"] = 30;
      map["sp"] = 31;
      map["pc"] = 32;

      return map;
  };

  const registerIndexMap = generateRegisterIndexMap();

  const filteredRegisters = registerKvPairs.map(([registerName, value]) => {
    const index = registerIndexMap[registerName];
    if (index === undefined || index > 32) return null;
    if (isNullish(contextValidity)) {
      return [registerName, value];
    }

    return (BigInt(contextValidity) & (1n << BigInt(index))) ? [registerName, value] : null;
  }).filter(item => item !== null); // 移除null项

  const processedRegisters = isAndroid(pid) ? filteredRegisters : registerKvPairs;

  const rightColRef = useRef(null);
  const [asmCodeExplainModalVisible, setAsmCodeExplainModalVisible] = useState(false);
  const [asmCodeExplainText, setAsmCodeExplainText] = useState('');
  const [asmCodeExplainLoading, setAsmCodeExplainLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [maxHeight, setMaxHeight] = useState('50px');

  useEffect(() => {
    if (rightColRef.current) {
      setMaxHeight(`${rightColRef.current.clientHeight}px`);
    }
  }, [hasAsmCode, hasStackVariable]);

  const rvaHex = useMemo(() => {
    const matchRes = (rva || '').match('0x[0-9A-Fa-f]+');
    return matchRes?.[0] || '';
  }, [rva]);

  const stackVariableList = useMemo(() => {
    if (!stackVariableJsonStr) {
      return [];
    }
    try {
      return JSON.parse(stackVariableJsonStr);
    } catch (e) {
      console.error('parse stackVariable failed. ', e);
      return [];
    }
  }, [stackVariableJsonStr]);

  const asmCodeLineDoms = useMemo(() => {
    const asmCodeLines = (asmCodeInfo || '').split('\n').map(x => x.trimEnd());
    return asmCodeLines.map((x, i) => {
      const isRvaHexMatched = rvaHex && x.startsWith(rvaHex);
      return <div
        key={i}
        style={isRvaHexMatched ? { color: CS_STYLES.PRIMARY_COLOR_ORANGE } : undefined }
      >{ x }</div>
    });
  }, [asmCodeInfo, rvaHex]);

  const hasStackVariable = !!stackVariableList && stackVariableList.length > 0;

  const hasAsmCode = !!asmCodeInfo;

  function onClickExplainAsmCodeByAi() {
    // 汇编分析：使用 crashId + lineNumber 作为 trigger，相同代码行关闭后再打开保持状态
    aiDrawerSlice.startAsmAnalysis(crashId, asmCodeInfo, {
      trigger: `asm-${crashId}-${lineNumber}`,
    });
  }

  /*function onClickExplainAsmCodeByAi({ clearCache } = {}) {
    if (isNotNullish(abortController)) {
      abortController.abort();
      setAbortController(null);
    }
    setAsmCodeExplainModalVisible(true);
    setAsmCodeExplainText('');
    setAsmCodeExplainLoading(true);
    const url = '/api/aiPilot/explainAssemblyCode';
    const params = {
      appId,
      crashId,
      code: asmCodeInfo,
      clearCache,
    };
    const ac = new AbortController();
    setAbortController(ac);
    RestHelper.postEventStream(url, params, {
      signal: ac.signal,
      statusCodeToCallback: {
        500() {
          message.error(ze('后台正忙，请稍后重试','The server is busy, please try again later'));
        },
      },
      onmessage(event) {
        const rawData = event.data;
        if (!rawData) {
          return;
        }
        let data = rawData;
        try {
          data = JSON.parse(rawData);
        } catch (e) {
          console.error(`explainAsmCodeByAi error when parsing: `, rawData);
        }

        if (data) {
          setAsmCodeExplainLoading(false);
          setAsmCodeExplainText(oldValue => {
            if (!!data.trim() || !!oldValue) {
              return oldValue + data;
            } else {
              return oldValue;
            }
          });
        }
      },
    });
  } */

  if (!detailStackMemoryInfo) {
    return null;
  }

  const asmCodeExplainModal = <Modal
    title={ze('AI解析反汇编', 'Explain assembly code with AI')}
    visible={asmCodeExplainModalVisible}
    width='1000px'
    footer={null}
    maskClosable={false}
    onCancel={() => {
      if (isNotNullish(abortController)) {
        abortController.abort();
        setAbortController(null);
      }
      setAsmCodeExplainModalVisible(false);
    }}
  >
    { asmCodeExplainLoading && <div>{ ze('正在生成结果...', 'Generating...') }</div> }
    { !asmCodeExplainLoading && <div>
      <Input.TextArea
        style={{
          maxHeight: '70vh',
          fontFamily: 'Consolas, Menlo, Monaco, “DejaVu Sans Mono”, “Courier New”, monospace',
      }}
        value={asmCodeExplainText}
        readOnly={true}
        autoSize={{
          minRows: 8,
        }}
      />
    </div> }
    { isSuper && <Button
      onClick={() => onClickExplainAsmCodeByAi({ clearCache: true })}
    >（超管）清除结果缓存</Button>}
  </Modal>;

  return <div className={scss.stackLineMemoryInfo}>
    <Row gutter={16}>
      { isMinidump && <Col span={16}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <IconMemory />
          <div>Memory</div>
        </div>
        <pre
          style={{ marginTop: '12px', width: '650px', maxHeight: maxHeight, overflow: 'auto' }}
        >{ stackMemoryInfo }</pre>
      </Col> }
      <Col span={8} ref={rightColRef}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IconMemory />
            <div>Assembly Code</div>
          </div>
          { hasAsmCode && <Tooltip
            title={ze('用AI解析反汇编', 'Explain assembly code with AI')}
          >
            <Button
              style={{
                width:'32px',
                height:'32px',
                paddingTop:'6px',
              }}
              icon={<StackAi />}
              onClick={() => onClickExplainAsmCodeByAi({ clearCache: false })}
            />
          </Tooltip> }
        </div>
        { hasAsmCode && <pre style={{ width: '100%', padding: '8px 20px' }}>{ asmCodeLineDoms }</pre> }
        { !hasAsmCode && <pre style={{ width: '100%', padding: '8px 20px' }}>(Not Collected)</pre> }
        { hasStackVariable && <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <IconMemory />
          <div>Stack Variables</div></div> }
        { hasStackVariable && <pre style={{ width: '100%', padding: '8px 20px' }}>{
          stackVariableList.map(({ variableName, variableValue, location }, i) => <div key={i}>
            <span style={{ marginRight: '1em' }}>{ variableName }:</span>
            <span>{ variableValue }</span>
            { location && <span style={{ marginLeft: '1.5em' }}>{ location }</span> }
          </div>)
        }</pre> }
      </Col>
    </Row>
    { hasCurrentLineRegisterParseResult && <div style={{ marginTop: '16px', paddingRight: '40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <IconMemory />
        <div>Registers</div>
      </div>
      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        { RegisterInfoUtil.makeRegisterDom(processedRegisters) }
      </div>
    </div> }
    { asmCodeExplainModal }
  </div>;
};

StackLineMemoryInfo.propTypes = {
  /**
   * @type { { lineNumber: number, lineStack: string, stackMemoryInfo: string } }
   * */
  detailStackMemoryInfo: PropTypes.object,
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(StackLineMemoryInfo);
